import { useState } from 'react'
import useTemplateViewModel from './TemplateViewModel'

import ConfirmationModal from '@components/shared/ConfirmationModal'
import TemplateNewPlaceholderForm from './TemplateNewPlaceholderForm'
import TemplatePlaceholdersTable from './TemplatePlaceholdersTable'
import TemplateFieldsList from './TemplateFieldsList'
import { Link } from 'react-router-dom'
import { MdInfoOutline, MdDriveFileRenameOutline, MdOutlineSaveAs } from 'react-icons/md'

const Template = () => {
    
    const [hasModifiedFields, setHasModifiedFields] = useState(false)
    const [selectedTemplateFields, setSelectedTemplateFields] = useState([])
    
    const vm = useTemplateViewModel()
    
    return (
        
        <div className="Template">
            
            <div className="max-w-screen-lg mx-auto">
                <div className="flex items-center content-center justify-between">
                    
                    <div className="flex items-center content-center">
                        <h3 className="flex items-center content-center text-xl">
                            <Link
                                className="opacity-60"
                                to={`/settings/orgs/${vm.organization?.id}/templates`}>
                                Export Templates
                            </Link>
                            <span className="ml-1 text-lg opacity-60">/</span>
                            {vm.pendingTemplateRename
                                ? <input
                                    className="ml-2 py-0 border border-base-300/50
                                        outline-none bg-transparent rounded text-xl"
                                    value={vm.pendingTemplateRename}
                                    onKeyUp={e => {
                                        if (e.keyCode === 27)
                                            vm.setPendingTemplateRename(null)
                                    }}
                                    onChange={e => {
                                        vm.setPendingTemplateRename(e.target.value)
                                    }}/>
                                : <span className="ml-2">{vm.template?.name}</span>}
                        </h3>
                        <button
                            className="w-auto opacity-50 btn btn-link hover:opacity-100"
                            title={vm.pendingTemplateRename ? 'Save Changes' : 'Rename Export Template'}
                            {...vm.disabledProps}
                            onClick={vm.onRenameTemplateClick}>
                            {vm.pendingTemplateRename
                                ? <MdOutlineSaveAs className="text-2xl" />
                                : <MdDriveFileRenameOutline className="text-2xl" />}
                        </button>
                    </div>
                    
                    <div className="flex items-center content-center justify-end">
                        
                        <div className="flex items-center content-center justify-end">
                            <span>Starting Column</span>
                            <select
                                className="w-auto pr-6 ml-3 select"
                                value={vm.startingCol}
                                {...vm.disabledProps}
                                onChange={vm.onStartingColChange}>
                                {vm.startingColOptions.map(it => (
                                    <option key={`starting-col-option-${it}`} value={it}>
                                        {it}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="flex items-center content-center justify-end ml-3">
                            <span>Starting Row</span>
                            <select
                                className="w-auto pr-6 ml-3 select"
                                value={vm.startingRow}
                                {...vm.disabledProps}
                                onChange={vm.onStartingRowChange}>
                                {vm.startingRowOptions.map(it => (
                                    <option key={`starting-row-option-${it}`} value={it}>
                                        {it}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="flex items-center content-center justify-end ml-3">
                            <span>Workbook Sheet</span>
                            <select
                                className="w-auto pr-6 ml-3 select"
                                value={vm.workbookSheet}
                                {...vm.disabledProps}
                                onChange={vm.onWorkbookSheetChange}>
                                {vm.template?.sheets?.map(it => (
                                    <option key={`workbook-sheet-option-${it}`} value={it}>
                                        {it}
                                    </option>
                                ))}
                            </select>
                        </div>
                    
                    </div>
                </div>
            </div>
            
            <div className="max-w-screen-lg mx-auto">
                <div className="flex items-center content-center justify-between">
                    <h3 className="text-lg">
                        Placeholders
                    </h3>
                    <div className="flex items-center content-center justify-end">
                        <button
                            className="btn btn-link"
                            onClick={() => vm.setPlaceholdersInfoModalOpen(true)}>
                            <MdInfoOutline className="text-2xl" />
                        </button>
                    </div>
                </div>
                <TemplateNewPlaceholderForm
                    className="mt-3"
                    refNewPlaceholderKey={vm.refNewPlaceholderKey}
                    templatePlaceholders={vm.templatePlaceholders}
                    newPlaceholderKey={vm.newPlaceholderKey}
                    setNewPlaceholderKey={vm.setNewPlaceholderKey}
                    newPlaceholderValue={vm.newPlaceholderValue}
                    onPlaceholderValueChange={vm.onPlaceholderValueChange}
                    onAddPlaceholderClick={vm.onAddPlaceholderClick}
                    onAddDefaultPlaceholdersClick={vm.onAddDefaultPlaceholdersClick}
                    disabledProps={vm.disabledProps} />
                <TemplatePlaceholdersTable
                    className="mt-4"
                    template={vm.template}
                    isInternalTemplate={vm.isInternalTemplate}
                    setPendingDeletePlaceholder={vm.setPendingDeletePlaceholder} />
            </div>
            
            <div className="max-w-screen-lg mx-auto mt-4">
                <header className="flex items-center content-center justify-between">
                    <h3 className="text-lg">
                        Export Fields
                    </h3>
                    <div className="flex items-center content-center justify-end">
                        <button
                            className="ml-3 btn btn-primary"
                            disabled={!hasModifiedFields || vm.isUpdatingTemplateFields}
                            onClick={() => vm.updateTemplateFields(selectedTemplateFields)}>
                            {vm.isUpdatingTemplateFields && <span className="loading loading-spinner loading-xs" />}
                            Save Fields
                        </button>
                        <button
                            className="btn btn-link"
                            onClick={() => vm.setFieldsInfoModalOpen(true)}>
                            <MdInfoOutline className="text-2xl" />
                        </button>
                    </div>
                </header>
                <TemplateFieldsList
                    className="mt-4"
                    template={vm.template}
                    onDirtyChange={setHasModifiedFields}
                    onSelectedChange={setSelectedTemplateFields} />
            </div>
            
            <ConfirmationModal
                showModal={vm.placeholdersInfoModalOpen}
                showHideModal={vm.setPlaceholdersInfoModalOpen}
                title="Placeholders"
                message={(<>
                    <p>
                        Placeholders are arbitrary variables you can define in your export runsheet template
                        that will be replaced with the corresponding data value on export.
                    </p>
                    <p className="mt-4">
                        Unlike <i>fields</i>, placeholders do not need to appear within the data rows &amp; columns.
                        This can be useful for things like top banners or bottom footers.
                    </p>
                </>)} />
            
            <ConfirmationModal
                showModal={vm.fieldsInfoModalOpen}
                showHideModal={vm.setFieldsInfoModalOpen}
                title="Placeholders"
                message={(<>
                    <p>Fields are data columns within the export runsheet that repeat for each row.</p>
                    <p className="mt-4">
                        You can choose which fields should be exported for each export template.
                        If no fields are selected, all fields will be exported.
                        You can also customize the order of the fields by dragging & dropping.
                    </p>
                </>)} />
            
            <ConfirmationModal
                showModal={Boolean(vm.pendingDeletePlaceholder)}
                showHideModal={vm.setPendingDeletePlaceholder}
                title="Delete Template Placeholder"
                message={`Are you sure you want to delete the placeholder "${vm.pendingDeletePlaceholder?.key}"`}
                onConfirm={vm.onConfirmDeletePlaceholderClick} />
        
        </div>
        
    )
    
}

export default Template
